import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import {
  calculateNetWeight,
  convertWeight,
  TouchNumberField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { ItemFieldProps } from '../_types';

import {
  $lastFocusedRowId,
  $lastFocusedWeightFieldType,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTakeWeightListener } from '~/modules/trade/hooks/useScaleReading';
import { useScaleTakeWeight } from '~/modules/trade/hooks/useScaleWeight';
import { useWeightConversion } from '~/modules/trade/hooks/useWeightConversion';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { toWeight } from '~/modules/trade/utils/weight';
import { useUIPreferences } from '~/shared/hooks';

//
// ⚛️ ⚛️ ⚛️ GROSS WEIGHT FIELD ⚛️ ⚛️ ⚛️
//
export const GrossWeightField: React.FC<ItemFieldProps> = ({
  lineItem,
  disabled,
  onUpdateItem,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
  onKeyDown,
}) => {
  const t = useTranslations();

  // Atoms
  const lastFocusedRowId = useAtomValue($lastFocusedRowId);
  const lastFocusedWeightFieldType = useAtomValue($lastFocusedWeightFieldType);
  const setLastFocusedWeightFieldType = useSetAtom($lastFocusedWeightFieldType);

  const { touchModeEnabled } = useUIPreferences();
  const { convertToDisplayUnit, convertFromDisplayUnit, massUnitAbbreviation } =
    useWeightConversion({
      weightUnit,
      facilityDefaultUnit,
      pricingUnit: (lineItem.pricingUnit as Unit) ?? enumUnit.Pounds,
    });

  const grossWeight = lineItem.weights?.[enumLoadWeightType.Gross];
  const tareWeight = lineItem.weights?.[enumLoadWeightType.Tare];
  const storedUnit = (grossWeight?.unit as Unit) ?? enumUnit.Pounds;

  // Memoized values
  const displayValue = React.useMemo(() => {
    if (!grossWeight) return 0;

    const gross = toWeight(grossWeight.value, grossWeight.unit);

    return convertToDisplayUnit(gross);
  }, [grossWeight, convertToDisplayUnit]);

  const isQuantityBased = lineItem.pricingUnit === enumUnit.Each;
  const isOutboundTaggedMaterial =
    lineItem.type === 'Tagged' && !!lineItem.tagId;

  // Callbacks
  const handleGrossWeightChange = React.useCallback(
    (value: number) => {
      const convertedValue = convertFromDisplayUnit(value ?? 0, storedUnit);

      const newGrossWeight = toWeight(convertedValue, storedUnit);

      onUpdateItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Gross]: newGrossWeight,
          [enumLoadWeightType.Net]: calculateNetWeight(
            newGrossWeight,
            tareWeight ?? null,
          ),
        },
      });
    },
    [
      lineItem.id,
      lineItem.weights,
      tareWeight,
      storedUnit,
      convertFromDisplayUnit,
      onUpdateItem,
    ],
  );

  const { takeWeight } = useScaleTakeWeight();

  const applyScaleReading = React.useCallback(
    (scaleValue: number, scaleUnit: Unit) => {
      const convertedValue = convertWeight(scaleValue, scaleUnit, storedUnit);
      const newGrossWeight = toWeight(convertedValue, storedUnit);

      onUpdateItem(lineItem.id, {
        weights: {
          ...lineItem.weights,
          [enumLoadWeightType.Gross]: newGrossWeight,
          [enumLoadWeightType.Net]: calculateNetWeight(
            newGrossWeight,
            tareWeight ?? null,
          ),
        },
      });
    },
    [lineItem.id, lineItem.weights, tareWeight, storedUnit, onUpdateItem],
  );

  useTakeWeightListener(
    ({ weight, unitOfMeasurement }) => {
      if (
        lastFocusedRowId === lineItem.id &&
        lastFocusedWeightFieldType === 'gross'
      ) {
        applyScaleReading(weight, unitOfMeasurement);
      }
    },
    [
      lastFocusedRowId,
      lastFocusedWeightFieldType,
      lineItem.id,
      applyScaleReading,
    ],
  );

  const handleFocus = React.useCallback(() => {
    setLastFocusedWeightFieldType('gross');
  }, [setLastFocusedWeightFieldType]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === ' ') {
        e.preventDefault();
        const reading = takeWeight();
        if (reading?.stable) {
          applyScaleReading(
            reading.grossWeight,
            reading.unitOfMeasurement ?? enumUnit.Pounds,
          );
        }
        return;
      }
      onKeyDown?.(e);
    },
    [takeWeight, applyScaleReading, onKeyDown],
  );

  return (
    <TouchNumberField
      aria-label={t.scaleTicketPro.columns.grossWeight}
      isDisabled={isOutboundTaggedMaterial || disabled}
      value={displayValue}
      onChange={handleGrossWeightChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      formatOptions={{ minimumFractionDigits: 0, maximumFractionDigits: 2 }}
      touchModeEnabled={touchModeEnabled}
      suffix={massUnitAbbreviation}
      prefix={
        isQuantityBased
          ? undefined
          : t.scaleTicketPro.itemsInterface.grossWeightPrefix
      }
      className="@GrossWeightField"
    />
  );
};
